<template>
    <editor
        v-model="model"
        :editor-style="props.editorStyle"
        @load="handleEditorLoad"
    />
</template>

<script setup lang="ts">
import Editor from 'primevue/editor';
import { computed, onBeforeUnmount } from 'vue';
import { IRichTextPendingImage } from '@/interfaces/deltaGpt/IRichTextImage.ts';

interface IQuillToolbar {
    addHandler: (name: string, handler: () => void) => void;
}

interface IQuillEditor {
    getLength: () => number;
    getModule: (name: string) => IQuillToolbar | null;
    getSelection: (focus?: boolean) => { index: number; length: number } | null;
    insertEmbed: (index: number, type: string, value: string, source?: string) => void;
    root: HTMLElement;
    setSelection: (index: number, length?: number, source?: string) => void;
}

const props = defineProps<{
    modelValue: string;
    editorStyle?: string;
}>();

const emit = defineEmits<{
    'update:modelValue': [string];
    load: [unknown];
}>();

const model = computed<string>({
    get: () => props.modelValue,
    set: value => {
        deleteRemovedLocalImages(props.modelValue, value);
        emit('update:modelValue', value);
    },
});

const pendingImages = new Map<string, IRichTextPendingImage>();

function handleEditorLoad (event: unknown): void {
    emit('load', event);
    const quill = getQuillEditor(event);
    if (!quill) return;

    const toolbar = quill?.getModule('toolbar');
    toolbar?.addHandler('image', () => {
        uploadAndInsertImage(quill);
    });
}

function getQuillEditor (event: unknown): IQuillEditor | null {
    if (!event || typeof event !== 'object') return null;
    const possibleEvent = event as { instance?: IQuillEditor; editor?: IQuillEditor };
    return possibleEvent.instance ?? possibleEvent.editor ?? null;
}

function uploadAndInsertImage (quill: IQuillEditor): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/gif,image/webp';
    input.onchange = async (): Promise<void> => {
        const file = input.files?.[0];
        if (!file) return;

        const temporaryUrl = await fileToDataUrl(file);
        const selection = quill.getSelection(true);
        const insertIndex = selection?.index ?? Math.max(0, quill.getLength() - 1);
        quill.insertEmbed(insertIndex, 'image', temporaryUrl, 'user');
        quill.setSelection(insertIndex + 1, 0, 'silent');
        pendingImages.set(temporaryUrl, {
            imageId: crypto.randomUUID(),
            temporaryUrl,
            file,
        });
        emit('update:modelValue', quill.root.innerHTML);
    };
    input.click();
}

function deleteRemovedLocalImages (oldHtml: string, newHtml: string): void {
    const oldImageSources = getImageSources(oldHtml);
    const nextImageSources = getImageSources(newHtml);

    oldImageSources.forEach(url => {
        if (!pendingImages.has(url) || nextImageSources.has(url)) return;

        pendingImages.delete(url);
    });
}

function getImageSources (html: string): Set<string> {
    const imageSources = new Set<string>();
    if (!html.trim()) return imageSources;

    const document = new DOMParser().parseFromString(html, 'text/html');
    document.querySelectorAll('img').forEach(image => {
        const src = image.getAttribute('src');
        if (src) imageSources.add(src);
    });

    return imageSources;
}

function getPendingImages (): Array<IRichTextPendingImage> {
    return Array.from(pendingImages.values());
}

function fileToDataUrl (file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (): void => resolve(reader.result?.toString() ?? '');
        reader.onerror = (): void => reject(reader.error);
        reader.readAsDataURL(file);
    });
}

onBeforeUnmount(() => {
    pendingImages.clear();
});

defineExpose({
    getPendingImages,
});
</script>
